// src/features/admin/AdminLayout.tsx
import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { logout } from "@/lib/api";
import { useAdminRole } from "./AdminGuard";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

const withBase = (p: string) => (import.meta.env.BASE_URL || "./") + p.replace(/^\/+/, "");
const LOGO = withBase("icons/ReadersNote_logo.svg");

export default function AdminLayout() {
    const nav = useNavigate();
    const { roles } = useAdminRole();
    const isAdmin      = roles.some(r => r === "ADMIN" || r === "SUPER_ADMIN");
    const isAuthor     = roles.includes("AUTHOR");
    const isMonitoring = roles.includes("MONITORING");
    const goLogout = () => { logout(); nav("/admin/login", { replace: true }); };

    return (
        <div style={{ display: "flex", height: "var(--app-lvh, 100vh)", background: COLOR.bgLighter, color: COLOR.text, fontFamily: "inherit" }}>
            {/* Sidebar */}
            <aside style={{
                width: 220, flexShrink: 0,
                background: COLOR.textDark, color: COLOR.borderAlt,
                display: "flex", flexDirection: "column",
                padding: "0 0 20px",
            }}>
                {/* Brand */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "24px 20px 20px",
                    borderBottom: "1px solid #27272a",
                }}>
                    <img src={LOGO} alt="" style={{ width: 30, height: 30, filter: "invert(1)" }} />
                    <span style={{ fontWeight: FW.extraBold, fontSize: 16, color: COLOR.white, letterSpacing: "-0.3px" }}>독서노트 Admin</span>
                </div>

                {/* Nav */}
                <nav style={{ flex: 1, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                    {(isAdmin) && <SideNavItem to="/admin/users" icon="👥">Users</SideNavItem>}
                    {(isAdmin || isAuthor) && <SideNavItem to="/admin/tox" icon="📋">Weekly Tox</SideNavItem>}
                    {(isAdmin || isMonitoring) && <SideNavItem to="/admin/attendance" icon="📊">출석 통계</SideNavItem>}
                    {(isAdmin || isMonitoring) && <SideNavItem to="/admin/books-stats" icon="📚">도서 통계</SideNavItem>}
                    {(isAdmin || isMonitoring) && <SideNavItem to="/admin/user-stats" icon="👤">사용자 통계</SideNavItem>}
                    {(isAdmin) && <SideNavItem to="/admin/keywords" icon="🏷️">리뷰 키워드</SideNavItem>}
                    {(isAdmin) && <SideNavItem to="/admin/book-stores" icon="🏬">구매처 관리</SideNavItem>}
                </nav>

                {/* Logout */}
                <div style={{ padding: "0 12px" }}>
                    <button
                        onClick={goLogout}
                        style={{
                            width: "100%", padding: "10px 14px",
                            background: "#27272a", color: COLOR.textLight,
                            border: "1px solid #3f3f46", borderRadius: RADIUS.sm,
                            cursor: "pointer", fontSize: 13, fontWeight: FW.medium,
                            display: "flex", alignItems: "center", gap: 8,
                            transition: "background 0.15s",
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = "#3f3f46")}
                        onMouseLeave={e => (e.currentTarget.style.background = "#27272a")}
                    >
                        <span>↩</span> 로그아웃
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Topbar */}
                <header style={{
                    height: 52, flexShrink: 0,
                    background: COLOR.white,
                    borderBottom: BORDER.light,
                    display: "flex", alignItems: "center",
                    padding: "0 24px",
                }}>
                    <Link to="/admin" style={{ textDecoration: "none", color: COLOR.textMuted, fontSize: 13, fontWeight: FW.medium }}>
                        Dashboard
                    </Link>
                </header>

                {/* Content */}
                <main style={{ flex: 1, overflowY: "auto", padding: "28px 32px" }}>
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

function SideNavItem({ to, icon, children }: { to: string; icon: string; children: React.ReactNode }) {
    return (
        <NavLink
            to={to}
            style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 12px", borderRadius: RADIUS.sm,
                textDecoration: "none", fontSize: 13, fontWeight: FW.medium,
                color: isActive ? COLOR.white : COLOR.textLight,
                background: isActive ? "#3f3f46" : "transparent",
                transition: "background 0.15s, color 0.15s",
            })}
            onMouseEnter={e => { if (!(e.currentTarget as HTMLElement).dataset.active) (e.currentTarget as HTMLElement).style.background = "#27272a"; }}
            onMouseLeave={e => { if (!(e.currentTarget as HTMLElement).dataset.active) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        >
            <span style={{ fontSize: 15 }}>{icon}</span>
            {children}
        </NavLink>
    );
}